import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { heroService } from '@/services/altingross';

// ─── Thunks ────────────────────────────────────────────────────────────────
export const fetchHeros = createAsyncThunk(
  'heros/fetchList',
  async ({ page = 1, size = 12, filter = null } = {}, { rejectWithValue }) => {
    try {
      const res = await heroService.getList(page, size, filter);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const fetchHero = createAsyncThunk(
  'heros/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const res = await heroService.getById(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const createHero = createAsyncThunk(
  'heros/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await heroService.create(data);
      return res.data; // { id }
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const updateHero = createAsyncThunk(
  'heros/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await heroService.update(id, data);
      return res.data; // { id }
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const deleteHero = createAsyncThunk(
  'heros/delete',
  async (id, { rejectWithValue }) => {
    try {
      await heroService.deleteById(id);
      return id;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────────
const herosSlice = createSlice({
  name: 'heros',
  initialState: {
    list: {
      data:    null,   // { page, size, count, hasNext, hasPrev, totalPages, items[] }
      loading: false,
      error:   null,
    },
    detail: {
      data:    null,
      loading: false,
      error:   null,
    },
    saveLoading:   false,
    saveError:     null,
    deleteLoading: false,
    deleteError:   null,
    deletingId:    null,
  },
  reducers: {
    resetDetail: (state) => {
      state.detail = { data: null, loading: false, error: null };
    },
    resetSave: (state) => {
      state.saveLoading = false;
      state.saveError   = null;
    },
  },
  extraReducers: (builder) => {
    // fetchHeros
    builder
      .addCase(fetchHeros.pending, (state) => {
        state.list.loading = true;
        state.list.error   = null;
      })
      .addCase(fetchHeros.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data    = action.payload;
      })
      .addCase(fetchHeros.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error   = action.payload;
      });

    // fetchHero
    builder
      .addCase(fetchHero.pending, (state) => {
        state.detail.loading = true;
        state.detail.error   = null;
      })
      .addCase(fetchHero.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.data    = action.payload;
      })
      .addCase(fetchHero.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error   = action.payload;
      });

    // createHero & updateHero — ortak save state
    const savePending   = (state) => { state.saveLoading = true;  state.saveError = null; };
    const saveFulfilled = (state) => { state.saveLoading = false; };
    const saveRejected  = (state, action) => { state.saveLoading = false; state.saveError = action.payload; };

    builder
      .addCase(createHero.pending,   savePending)
      .addCase(createHero.fulfilled, saveFulfilled)
      .addCase(createHero.rejected,  saveRejected)
      .addCase(updateHero.pending,   savePending)
      .addCase(updateHero.fulfilled, saveFulfilled)
      .addCase(updateHero.rejected,  saveRejected);

    // deleteHero
    builder
      .addCase(deleteHero.pending, (state, action) => {
        state.deleteLoading = true;
        state.deleteError   = null;
        state.deletingId    = action.meta.arg;
      })
      .addCase(deleteHero.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deletingId    = null;
        if (state.list.data?.items) {
          state.list.data.items = state.list.data.items.filter(i => i.id !== action.payload);
          state.list.data.count = Math.max(0, (state.list.data.count ?? 1) - 1);
        }
      })
      .addCase(deleteHero.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError   = action.payload;
        state.deletingId    = null;
      });
  },
});

export const { resetDetail, resetSave } = herosSlice.actions;
export default herosSlice.reducer;
